import { useEffect, useRef, useState } from 'react';

import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAddressSuggestions } from '../../hooks';
import type { AddressSuggestion } from '../../model/settingsTypes';

const DEBOUNCE_MS = 350;

export interface AddressAutocompleteFieldProps {
  /** The address text — controlled, updates on every keystroke via `onChange`. */
  value: string;
  /** Fires on free typing (every keystroke), so the caller always has the raw text. */
  onChange: (text: string) => void;
  /** Fires only when the merchant picks a suggestion from the dropdown. */
  onSelectSuggestion: (suggestion: AddressSuggestion) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
}

/**
 * Free-text address input backed by the BFF's `GET /api/v1/store/address-suggestions`
 * (DaData passthrough). `freeSolo` — a merchant must always be able to type an address the
 * geocoder doesn't resolve; this must never regress `LocationDialog`'s previous plain-text
 * fallback capability. Debounces ~350ms before querying and gates the actual request behind a
 * 3-character minimum (via `useAddressSuggestions`), mirroring the UX of `telegram-catalog`'s
 * `AddressFormDialog` (debounce, min length, fail-silent, server-side-filtered-only results).
 */
export function AddressAutocompleteField({
  value,
  onChange,
  onSelectSuggestion,
  label,
  required = false,
  disabled = false,
  helperText,
  error = false,
}: AddressAutocompleteFieldProps) {
  const { t } = useTranslation('settings');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce is scheduled from `onInputChange`'s `'input'` branch below — i.e. only on a genuine
  // keystroke inside this field — NOT from a `useEffect` watching `value`. `value` is also
  // rewritten externally by the parent on every suggestion pick (`onSelectSuggestion` → sets
  // `draft.address` to the picked suggestion's own label) and on dialog-open (seeding `draft`
  // from the location being edited); a `useEffect([value])` can't distinguish those from typing
  // and would re-query the address the merchant just selected/already has 350ms later — wasted
  // upstream calls and a dropdown that can flash back open right after a pick. Prop changes don't
  // invoke `onInputChange` (it only fires for changes originating inside Autocomplete itself), so
  // gating scheduling there structurally excludes both cases. Only cleans up the pending timer on
  // unmount here.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const scheduleQuery = (text: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedQuery(text), DEBOUNCE_MS);
  };

  const suggestions = useAddressSuggestions(debouncedQuery);
  const options = suggestions.data?.suggestions ?? [];
  const loading = suggestions.isFetching;

  return (
    <Autocomplete<AddressSuggestion, false, false, true>
      freeSolo
      fullWidth
      disabled={disabled}
      inputValue={value}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.value)}
      noOptionsText={
        debouncedQuery.trim().length < 3
          ? t('storeProfileSettings.locationDialog.addressMinLengthHint')
          : t('storeProfileSettings.locationDialog.addressNoOptions')
      }
      loadingText={t('storeProfileSettings.locationDialog.addressLoadingText')}
      onInputChange={(_event, newValue, reason) => {
        if (reason === 'input') {
          onChange(newValue);
          scheduleQuery(newValue);
        }
        if (reason === 'clear') {
          if (timerRef.current) clearTimeout(timerRef.current);
          onChange('');
          setDebouncedQuery('');
        }
      }}
      onChange={(_event, newValue) => {
        if (newValue && typeof newValue !== 'string') onSelectSuggestion(newValue);
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Box sx={{ py: 0.25, minWidth: 0 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                {option.value}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          size="small"
          placeholder={t('storeProfileSettings.locationDialog.addressPlaceholder')}
          // `AutocompleteRenderInputParams.InputProps` (not `slotProps`) is what this MUI
          // version's `renderInput` callback actually hands us — verified against the
          // installed @mui/material types. Merge in a loading spinner alongside whatever
          // adornment Autocomplete already computed (its own clear button, etc.).
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
